/**
 * What each figure on the home page is made of (RUT-4071).
 *
 * Every headline number opens a popover: how it is calculated, where the
 * numbers come from, and the companies behind it. This module turns one
 * month of the overview response into that — the page only renders it. It is
 * unit tested for the same reason `economics.ts` is: a number nobody can
 * trace is a number nobody should quote.
 */

import type { AttentionItem, DirectCost, OverviewMonth, OverviewOrgMonth, OverviewOrgRow, OverviewResponse, WaitingOn } from './types';
import { formatCount, formatCurrency, formatPercent } from './format';

export type FigureKey = 'plan' | 'spread' | 'direct' | 'shared' | 'subscription' | 'overall';

export interface FigureLine {
  label: string;
  value: string;
  /** The result line of the calculation. */
  total?: boolean;
}

export interface FigureRow {
  name: string;
  note: string;
  value: string;
  /** Raw value, for the CSV. */
  amount: number;
}

export interface FigureDetail {
  key: FigureKey;
  title: string;
  total: string;
  lines: FigureLine[];
  /** Where the numbers come from, in plain words. */
  source: string;
  /** Something the figure provably adds up to, when there is one. */
  ties: string | null;
  rowsTitle: string;
  /** Every row, largest first. The popover shows the first few. */
  rows: FigureRow[];
}

/** How many rows a popover shows before "and N more". */
export const POPOVER_ROWS = 5;

interface PayingOrgMonth {
  org: OverviewOrgRow;
  month: OverviewOrgMonth;
}

function orgMonths(data: OverviewResponse, month: string, include: (org: OverviewOrgRow) => boolean): PayingOrgMonth[] {
  return data.byOrg
    .filter(include)
    .map((org) => ({ org, month: org.byMonth.find((m) => m.month === month) }))
    .filter((row): row is PayingOrgMonth => !!row.month);
}

function rowsBy(
  rows: PayingOrgMonth[],
  amount: (m: OverviewOrgMonth) => number,
  note: (m: OverviewOrgMonth) => string,
): FigureRow[] {
  return rows
    .map(({ org, month }) => ({
      name: org.name,
      note: note(month),
      amount: amount(month),
    }))
    .filter((row) => row.amount !== 0)
    .sort((a, b) => b.amount - a.amount)
    .map((row) => ({ ...row, value: formatCurrency(row.amount) }));
}

/**
 * The largest of a company's direct costs — shared by Home's popover and the
 * Costs page so a company gets the same driver on both. Null when it caused nothing.
 */
export function biggestDriver(cost: DirectCost): { label: string; value: number } | null {
  const parts: Array<[string, number]> = [
    ['texts', cost.texts],
    ['visits', cost.visits],
    ['AI', cost.ai],
    ['crew GPS', cost.gps ?? 0],
  ];
  const [label, value] = parts.sort((a, b) => b[1] - a[1])[0];
  return value > 0 ? { label, value } : null;
}

function mostlyNote(cost: OverviewOrgMonth['directCost']): string {
  const driver = biggestDriver(cost);
  return driver ? `mostly ${driver.label}, ${formatCurrency(driver.value)}` : '';
}

/** The popover contents for one figure in one month. */
export function figureDetail(data: OverviewResponse, summary: OverviewMonth, key: FigureKey): FigureDetail {
  // The companies this month counts: paying today, or invoiced that month.
  const orgs = orgMonths(data, summary.month, () => true).filter((row) => row.month.counted);
  // Invoices from a company that has since left still count toward the month
  // they were issued — only the demo account's never do.
  const invoicedOrgs = orgMonths(data, summary.month, (org) => org.nonPayingReason !== 'demo');
  const countedCount = orgs.length;

  switch (key) {
    case 'plan':
      return {
        key,
        title: 'Plan revenue',
        total: formatCurrency(summary.planRevenue),
        lines: [
          {
            label: `${formatCount(summary.billing.invoiceCount)} invoices issued`,
            value: formatCurrency(summary.billing.invoiced),
            total: true,
          },
          { label: 'Paid', value: formatCurrency(summary.billing.collected) },
          {
            label: `Still open (${formatCount(summary.billing.openCount)})`,
            value: formatCurrency(summary.billing.open),
          },
          ...(summary.failedCharges === null
            ? []
            : [
                {
                  label: 'Monthly charges declined right now',
                  value: summary.failedCharges > 0 ? `${formatCount(summary.failedCharges)} not invoiced` : 'None',
                },
              ]),
          { label: 'What our plan model expected', value: formatCurrency(summary.saasRevenue) },
        ],
        source:
          'The invoices we issued this month, by the day each was billed. A declined monthly charge issues no invoice, so it is counted separately.',
        ties: `Adds up to the ${formatCount(summary.billing.invoiceCount)} invoices issued`,
        rowsTitle: 'By company',
        rows: rowsBy(
          invoicedOrgs,
          (m) => m.invoiced,
          () => '',
        ),
      };
    case 'spread':
      return {
        key,
        title: 'Ruta Pay spread',
        total: formatCurrency(summary.rutaPaySpread),
        lines: [
          {
            label: `Charged on ${formatCurrency(summary.rutaPay.volume)} of card payments`,
            value: formatCurrency(summary.rutaPay.feesCharged),
          },
          {
            label: `What those payments cost us (${summary.cardCostPercent}%)`,
            value: `− ${formatCurrency(summary.rutaPay.cardCost)}`,
          },
          {
            label: 'Spread kept',
            value: formatCurrency(summary.rutaPaySpread),
            total: true,
          },
        ],
        source:
          'Card payments taken through Ruta Pay by the companies this month counts. JustiFi’s fees are not our revenue or our cost — only the difference is ours.',
        ties: null,
        rowsTitle: 'By company',
        rows: rowsBy(
          orgs,
          (m) => m.rutaPaySpread,
          (m) => `${formatCurrency(m.cardVolume)} volume`,
        ),
      };
    case 'direct':
      return {
        key,
        title: 'Direct costs',
        total: formatCurrency(summary.directCost.total),
        lines: [
          { label: 'Texts', value: formatCurrency(summary.directCost.texts) },
          {
            label: 'Visit processing, about 1¢ a visit',
            value: formatCurrency(summary.directCost.visits),
          },
          {
            label: 'AI assistant',
            value: formatCurrency(summary.directCost.ai),
          },
          {
            label: 'Crew GPS',
            value: formatCurrency(summary.directCost.gps),
          },
          {
            label: 'Direct costs',
            value: formatCurrency(summary.directCost.total),
            total: true,
          },
        ],
        source:
          'Twilio’s per-message charges split by each company’s texts, about 1¢ of AWS per visit, the month’s AI bill split by each company’s share of metered tokens, and DynamoDB’s write charges (almost all crew location writes) split by the GPS segments each company wrote, or by crew hours past the location table’s 90 days. When Twilio reports no per-message charges, texts are estimated at unit rates. Only companies this month counts: billed today, or invoiced this month.',
        ties: null,
        rowsTitle: 'By company',
        rows: rowsBy(
          orgs,
          (m) => m.directCost.total,
          (m) => mostlyNote(m.directCost),
        ),
      };
    case 'shared':
      return {
        key,
        title: 'Shared costs',
        total: formatCurrency(summary.sharedCost.total),
        lines: [
          {
            label: 'Always-on AWS',
            value: formatCurrency(summary.sharedCost.alwaysOn),
          },
          {
            label: 'Flat vendor bills',
            value: formatCurrency(summary.sharedCost.vendors),
          },
          {
            label: 'Texting numbers and campaign fees',
            value: formatCurrency(summary.sharedCost.textingSetup),
          },
          {
            label: 'Companies this month doesn’t count',
            value: formatCurrency(summary.sharedCost.nonPaying),
          },
          {
            label: 'Staging and dev AWS',
            value:
              data.otherEnvironments?.status === 'connected'
                ? formatCurrency(summary.sharedCost.otherEnvironments)
                : 'Not connected yet',
          },
          {
            label: 'Shared costs',
            value: formatCurrency(summary.sharedCost.total),
            total: true,
          },
        ],
        source:
          'Production’s AWS bill less visit processing, crew GPS and AI; staging and dev’s AWS bills once connected; flat vendor bills; Twilio less its per-message charges; and what companies this month doesn’t count cost to serve (demo, never billed, switched off). Flat bills count as a full month even while the month is running.',
        ties: null,
        rowsTitle: 'Flat vendor bills',
        rows: data.fixedSaasCosts
          .filter((s) => s.monthlyCost > 0)
          .map((s) => ({
            name: s.service,
            note: s.category,
            amount: s.monthlyCost,
            value: formatCurrency(s.monthlyCost),
          }))
          .sort((a, b) => b.amount - a.amount),
      };
    case 'subscription':
      return {
        key,
        title: 'Subscription margin',
        total: `${formatCurrency(summary.subscriptionMargin)} · ${formatPercent(summary.subscriptionMarginPct, 0)}`,
        lines: [
          { label: 'Plan revenue', value: formatCurrency(summary.planRevenue) },
          {
            label: 'Ruta Pay spread',
            value: formatCurrency(summary.rutaPaySpread),
          },
          {
            label: 'Direct costs',
            value: `− ${formatCurrency(summary.directCost.total)}`,
          },
          {
            label: 'Subscription margin',
            value: formatCurrency(summary.subscriptionMargin),
            total: true,
          },
        ],
        source:
          'Each company’s revenue less only the costs that company caused. Shared costs are left out on purpose. Revenue includes invoices still open.',
        ties: `Sum of the ${formatCount(countedCount)} companies this month counts`,
        rowsTitle: 'By company',
        rows: rowsBy(
          orgs,
          (m) => m.subscriptionMargin,
          (m) => (m.revenueToRuta > 0 ? formatPercent(m.subscriptionMargin / m.revenueToRuta, 0) : ''),
        ),
      };
    case 'overall': {
      const sharedEach = countedCount > 0 ? summary.sharedCost.total / countedCount : 0;
      return {
        key,
        title: 'Overall margin',
        total: `${formatCurrency(summary.overallMargin)} · ${formatPercent(summary.overallMarginPct, 0)}`,
        lines: [
          {
            label: 'Subscription margin',
            value: formatCurrency(summary.subscriptionMargin),
          },
          {
            label: 'Shared costs',
            value: `− ${formatCurrency(summary.sharedCost.total)}`,
          },
          {
            label: 'Overall margin',
            value: formatCurrency(summary.overallMargin),
            total: true,
          },
        ],
        source: `Subscription margin less every shared cost. Per company, shared costs split evenly: ${formatCurrency(sharedEach)} each.`,
        ties: null,
        rowsTitle: 'By company, after an even share of shared costs',
        rows: rowsBy(
          orgs,
          (m) => m.subscriptionMargin - sharedEach,
          () => '',
        ),
      };
    }
  }
}

/** A CSV of every row behind a figure, for the popover's download link. */
export function figureCsv(detail: FigureDetail): string {
  const escape = (value: string) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  const lines = [['Name', 'Note', 'Amount'], ...detail.rows.map((r) => [r.name, r.note, r.amount.toFixed(2)])];
  return lines.map((cells) => cells.map(escape).join(',')).join('\n');
}

/** Whole days between an ISO date and now, never negative. */
export function daysSince(iso: string | null, now: Date): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000));
}

export const WAITING_ON_LABEL: Record<WaitingOn, string> = {
  customer: 'The organization',
  justifi: 'JustiFi',
  telnyx: 'Telnyx',
  us: 'Us',
};

/** Change from one value to the next, as a fraction; null without a base. */
export function changeFrom(before: number, after: number): number | null {
  if (before === 0) return null;
  return (after - before) / before;
}

export interface AttentionGroup {
  orgId: string;
  name: string;
  contactEmail: string | null;
  /** Everything stuck for this organization, oldest first. */
  items: AttentionItem[];
}

/**
 * One row per organization: its stuck items together, rows ordered by each
 * organization's oldest item (the list arrives oldest first already).
 */
export function groupAttentionByOrg(items: AttentionItem[]): AttentionGroup[] {
  const groups = new Map<string, AttentionGroup>();
  for (const item of items) {
    const group = groups.get(item.orgId) ?? {
      orgId: item.orgId,
      name: item.name,
      contactEmail: item.contactEmail,
      items: [],
    };
    group.items.push(item);
    groups.set(item.orgId, group);
  }
  return [...groups.values()];
}
